import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { activitiesData, ActivityData } from '../../data/activityData';

interface Activity {
  id: string;
  name: string;
  image: string;
  link: string;
}

const BrowseActivities: React.FC = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Generate activities dynamically from activityData
  const getActivities = (): Activity[] => {
    return Object.values(activitiesData).map((activityData: ActivityData) => {
      // Map activity IDs to translation keys
      const translationKeyMap: Record<string, string> = {
        'wild_life_spotting': 'landing.activities.deerWatching.title',
        'cantina_tilli': 'landing.activities.wineTasting.title',
        'arrosticini_mucciante': 'landing.activities.arrosticiniMucciante.title',
        'sextantio': 'landing.activities.sextantio.title'
      };

      const translationKey = translationKeyMap[activityData.id];
      const translatedName = translationKey ? t(translationKey) : activityData.name;


      return {
        id: activityData.id,
        name: translatedName,
        image: activityData.image,
        link: `/activities/${activityData.id}`
      };
    });
  };

  const activities = getActivities();
  
  // Check if we need slider navigation (more than 3 activities)
  const needsSlider = activities.length > 3;

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % Math.ceil(activities.length / 3));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + Math.ceil(activities.length / 3)) % Math.ceil(activities.length / 3));
  };

  const renderActivityCard = (activity: Activity) => (
    <div key={activity.id} className="col flex-1 min-w-0">
      <div className="img-holder rounded-lg overflow-hidden shadow-lg">
        <Link to={activity.link}>
          <img 
            src={activity.image} 
            alt={activity.name}
            className="w-full h-64 object-cover transition-transform duration-300 hover:scale-105"
          />
        </Link>
      </div>
      <h2 className="activities-title text-center mt-4">
        <Link 
          to={activity.link} 
          className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
        >
          {activity.name}
        </Link>
      </h2>
    </div>
  );

  return (
    <section id="activities" className="activities-section py-16 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <header className="section-header text-center mb-12">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-4">
            {t('landing.activities.title')}
          </h2>
          <div className="section-content">
            <p className="text-gray-600 text-lg">
              {t('landing.activities.subtitle')}
            </p>
          </div>
        </header>

        {/* Mobile: List View */}
        {isMobile && (
          <div className="grid grid-cols-1 gap-8 max-w-4xl mx-auto">
            {activities.map(renderActivityCard)}
          </div>
        )}

        {/* Desktop/Tablet: Slider View */}
        {!isMobile && (
          <div className="max-w-7xl mx-auto">
            {needsSlider ? (
              <>
                {/* Slider Container with Side Navigation */}
                <div className="flex items-center gap-4">
                  {/* Left Navigation Button */}
                  <button
                    onClick={prevSlide}
                    className="flex-shrink-0 bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 border border-gray-200"
                    aria-label="Previous activities"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Slider Content */}
                  <div className="flex-1 overflow-hidden">
                    <div 
                      className="flex transition-transform duration-500 ease-in-out"
                      style={{ 
                        transform: `translateX(-${currentSlide * 100}%)`
                      }}
                    >
                      {Array.from({ length: Math.ceil(activities.length / 3) }, (_, slideIndex) => {
                        const slideActivities = activities.slice(slideIndex * 3, slideIndex * 3 + 3);
                        const remainingSlots = 3 - slideActivities.length;
                        
                        return (
                          <div key={slideIndex} className="flex gap-8 w-full flex-shrink-0">
                            {slideActivities.map(renderActivityCard)}
                            {/* Fill remaining slots with empty divs to maintain layout */}
                            {Array.from({ length: remainingSlots }, (_, index) => (
                              <div key={`empty-${index}`} className="col flex-1 min-w-0 opacity-0">
                                <div className="img-holder rounded-lg overflow-hidden shadow-lg">
                                  <div className="w-full h-64 bg-transparent"></div>
                                </div>
                                <h2 className="activities-title text-center mt-4">
                                  <div className="text-2xl font-bold text-transparent">Placeholder</div>
                                </h2>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Right Navigation Button */}
                  <button
                    onClick={nextSlide}
                    className="flex-shrink-0 bg-white hover:bg-gray-50 text-gray-800 p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110 border border-gray-200"
                    aria-label="Next activities"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Dots Indicator */}
                <div className="flex justify-center mt-8 space-x-2">
                  {Array.from({ length: Math.ceil(activities.length / 3) }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-4 h-4 rounded-full transition-all duration-200 ${
                        index === currentSlide 
                          ? 'bg-blue-600 scale-125' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              /* Centered Grid Layout for 3 or fewer activities */
              <div className="flex justify-center">
                <div className="flex gap-8 max-w-4xl">
                  {activities.map(renderActivityCard)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export default BrowseActivities; 